






















































































































































import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import WindowEvent from "@/decorators/WindowEvent";
import Broadcaster from "@/models/SpecialInitiatives/Broadcaster";
import VIcon from "@/components/VIcon.vue";

type Kind = "TV" | "RADIO";

interface SpecialInitiativesColumn {
  value: string;
  label: string;
  isHighlight?: boolean;
}

interface SpecialInitiativesRow {
  subject: string;
  broadcastId?: number;
  broadcaster?: string;
  campaignableType?: string;
  totalWeeklySeconds?: number;
  kind: Kind | "";
  data: { [key: string]: { value: number; quote: string } };
  children: SpecialInitiativesRow[];
  isTotal: boolean;
}

type CollectionData = {
  weekRange: string;
  value: number;
  quote: number;
  companyIds: Set<string>;
  moduleName?: string;
  moduleTypology?: string;
  broadcastName?: string;
  broadcastId?: number;
  campaignableType?: string;
  totalWeeklySeconds?: number;
};
type SubjectCollection = { [key: string]: CollectionData[] };
type ParentRecord = {
  data: CollectionData[];
  subjects: SubjectCollection;
  mediaType: Kind;
};
type ParentCollection = { [key: string]: ParentRecord };

export interface SpecialInitiativesData {
  [key: string]: Broadcaster[];
}

export type SubjectColumn = "sales_agencies" | "editors" | "market_segments";

export interface WeekRange {
  name: string;
  label: string;
}

@Component({
  components: {
    VIcon,
  },
})
export default class SpecialInitiativesModulesTable extends Vue {
  @Prop({ required: true }) readonly weekRanges!: WeekRange[];
  @Prop({ required: true }) readonly value!: SpecialInitiativesData;
  @Prop({ required: true }) readonly subject!: SubjectColumn;
  @Prop({ default: false }) readonly loading!: boolean;
  @Prop({ required: true }) readonly grouped!: boolean;
  @Prop({ required: true }) readonly showModulesDetail!: boolean;
  @Prop({ required: true }) readonly quoted!: boolean;
  @Prop({ required: true }) readonly unitOfMeasurement!: string;

  @Ref() inner!: HTMLDivElement;
  @Ref() table!: HTMLTableElement;

  columns: SpecialInitiativesColumn[] = [];
  rows: SpecialInitiativesRow[] = [];
  mediaTypes: string[] = ["TV", "RADIO"];
  sortColumn = "";
  sortDirection: "ASC" | "DESC" | null = null;

  position = {
    overflow: false,
    scrolledToRight: false,
    scrolledToLeft: true,
  };

  mounted() {
    this.checkPosition();
    this.columns = this.loadColumns();
    this.convertValueToRows();
  }

  convertValueToRows() {
    const parents = this.convertBroadcastersToParentsCollection();
    const tvTotals: CollectionData[] = [];
    const radioTotals: CollectionData[] = [];
    const totals = this.computeParentTotals(parents, tvTotals, radioTotals);
    this.convertParentsToRow(parents, totals, tvTotals, radioTotals);
  }

  dataColumn(columnName: string) {
    return columnName.startsWith("col");
  }

  broadcasterKey(row: any, child: any) {
    return `${row.subject}-${child.subject}`;
  }

  moduleKey(row: any, child: any, childModule: any) {
    return `${row.subject}-${child.subject}-${childModule.subject}`;
  }

  setSort(column: SpecialInitiativesColumn) {
    if (this.sortColumn !== column.value) {
      this.sortColumn = column.value;
      this.sortDirection = "ASC";
    } else {
      switch (this.sortDirection) {
        case "ASC":
          this.sortDirection = "DESC";
          break;
        case "DESC":
          this.sortDirection = null;
          this.sortColumn = "";
          break;
        default:
          this.sortColumn = "ASC";
          break;
      }
    }
    if (this.sortDirection !== null) {
      this.rows.sort(this.sort.bind(this));
    } else {
      this.convertValueToRows();
    }
  }

  get clientCount() {
    return this.unitOfMeasurement === "clients_number";
  }

  get dataColumns() {
    return this.columns.filter((c) => this.dataColumn(c.value));
  }

  protected getColumnClass(column: SpecialInitiativesColumn) {
    if (this.sortDirection === null) return {};
    const columnClass = {
      "breakdown-table__column--sorted": column.value === this.sortColumn,
      "breakdown-table__column--sorted--asc":
        column.value === this.sortColumn && this.sortDirection === "ASC",
      "breakdown-table__column--sorted--desc":
        column.value === this.sortColumn && this.sortDirection === "DESC",
    };
    return columnClass;
  }

  isSorted(column: SpecialInitiativesColumn) {
    return this.sortColumn === column.value && this.sortDirection !== null;
  }

  private convertParentsToRow(
    parents: ParentCollection,
    totals: CollectionData[],
    tvTotals: CollectionData[],
    radioTotals: CollectionData[]
  ) {
    this.rows = [];
    for (const subject in parents) {
      const parent = parents[subject];
      const rowData: { [key: string]: { value: number; quote: string } } = {};
      this.dataColumns.forEach((column) => {
        const weekRange = column.value.split("-")[1];
        const total = totals.find((e) => e.weekRange === weekRange) || {
          weekRange: "",
          value: 1,
          quote: 1,
        };
        const element = parent.data.find((e) => e.weekRange === weekRange) || {
          weekRange,
          value: 0,
          quote: 0,
        };
        rowData[column.value] = {
          value: element.value,
          quote: Number((element.value / total.value) * 100).toFixed(0),
        };
      });
      const row: SpecialInitiativesRow = {
        subject,
        data: rowData,
        isTotal: false,
        kind: parent.mediaType,
        children: this.convertChildrenToRows(parent, totals),
      };
      this.rows.push(row);
    }
    if (tvTotals.length > 0) {
      this.rows.push(
        this.CollectionDataListToRow(tvTotals, "TOTALE TV", "TV", true)
      );
    }
    if (radioTotals.length > 0) {
      this.rows.push(
        this.CollectionDataListToRow(radioTotals, "TOTALE RADIO", "RADIO", true)
      );
    }
    this.rows.push(this.CollectionDataListToRow(totals, "TOTALE", "", true));
    this.rows.sort(this.sort.bind(this));
  }

  private CollectionDataListToRow(
    list: CollectionData[],
    name: string,
    kind: Kind | "",
    isTotal: boolean
  ) {
    const rowData: { [key: string]: { value: number; quote: string } } = {};
    this.dataColumns.forEach((column) => {
      const weekRange = column.value.split("-")[1];
      const element = list.find((e) => e.weekRange === weekRange) || {
        weekRange,
        value: 0,
        quote: 0,
      };
      rowData[column.value] = {
        value: element.value,
        quote: "100",
      };
    });
    const row: SpecialInitiativesRow = {
      subject: name,
      data: rowData,
      isTotal,
      children: [],
      kind,
    };
    return row;
  }

  private sortByColumn(
    a: SpecialInitiativesRow,
    b: SpecialInitiativesRow,
    columnName: string,
    direction: "ASC" | "DESC"
  ): (a: SpecialInitiativesRow, b: SpecialInitiativesRow) => -1 | 0 | 1 {
    return (a: SpecialInitiativesRow, b: SpecialInitiativesRow) => {
      let result: -1 | 0 | 1 = 0;
      if (columnName == "subject") {
        if (a.subject < b.subject) {
          result = direction === "ASC" ? -1 : 1;
        } else if (b.subject < a.subject) {
          result = direction === "ASC" ? 1 : -1;
        }
      } else {
        if (a.data[columnName] === undefined) {
          result = direction === "ASC" ? -1 : 1;
        } else if (b.data[columnName] === undefined) {
          result = direction === "ASC" ? 1 : -1;
        } else if (a.data[columnName].value < b.data[columnName].value) {
          result = direction === "ASC" ? -1 : 1;
        } else {
          result = direction === "ASC" ? 1 : -1;
        }
      }
      return result;
    };
  }

  private sort(a: SpecialInitiativesRow, b: SpecialInitiativesRow): -1 | 0 | 1 {
    if (this.sortDirection !== null) {
      return this.baseSort(
        a,
        b,
        this.sortByColumn(a, b, this.sortColumn, this.sortDirection)
      );
    }
    return this.baseSort(a, b);
  }

  private baseSort(
    a: SpecialInitiativesRow,
    b: SpecialInitiativesRow,
    otherCondition?: (
      a: SpecialInitiativesRow,
      b: SpecialInitiativesRow
    ) => -1 | 0 | 1
  ): -1 | 0 | 1 {
    let result: -1 | 0 | 1 = 0;
    if (a.isTotal && a.kind == "") {
      return 1;
    }
    if (b.isTotal && b.kind == "") {
      return -1;
    }
    if (a.kind == "TV" && b.kind == "RADIO") {
      result = -1;
    } else if (a.kind == "RADIO" && b.kind == "TV") {
      result = 1;
    }
    if (a.kind == b.kind) {
      if (a.isTotal) {
        result = 1;
      } else if (b.isTotal) {
        result = -1;
      } else {
        if (otherCondition) {
          result = otherCondition(a, b);
          const descendantsSort = (child: SpecialInitiativesRow) => {
            child.children?.sort(otherCondition);
          };
          a.children.sort(otherCondition);
          a.children.forEach(descendantsSort);
          b.children.sort(otherCondition);
          b.children.forEach(descendantsSort);
        }
      }
    }
    return result;
  }

  private convertChildrenToRows(
    parent: ParentRecord,
    databaseTotals: CollectionData[]
  ): SpecialInitiativesRow[] {
    const rows: SpecialInitiativesRow[] = [];
    for (const key in parent.subjects) {
      const totals =
        this.subject === "market_segments" ? parent.data : databaseTotals;
      const subject = parent.subjects[key];
      const rowData: { [key: string]: { value: number; quote: string } } = {};
      this.dataColumns.forEach((column) => {
        const weekRange = column.value.split("-")[1];
        const total = totals.find((e) => e.weekRange === weekRange) || {
          weekRange: "",
          value: 1,
          quote: 1,
        };
        const element = subject.find((e) => e.weekRange === weekRange) || {
          weekRange,
          value: 0,
          quote: 0,
        };

        const value = subject
          .filter((e) => e.weekRange === weekRange)
          .reduce((acc, collectionData) => {
            return acc + collectionData.value;
          }, 0);
        rowData[column.value] = {
          value: value,
          //value: element.value,
          quote: Number((element.value / total.value) * 100).toFixed(0),
        };
      });
      const row: SpecialInitiativesRow = {
        subject: key,
        data: rowData,
        isTotal: false,
        kind: parent.mediaType,
        children: this.showModulesDetail
          ? this.buildChildrenModulesRows(key, parent)
          : [],
      };
      rows.push(row);
    }
    return rows;
  }

  private buildChildrenModulesRows(
    subjectKey: string,
    parent: ParentRecord
  ): SpecialInitiativesRow[] {
    let rows: SpecialInitiativesRow[] = [];
    for (const subject of parent.subjects[subjectKey]) {
      const rowData: { [key: string]: { value: number; quote: string } } = {};
      this.dataColumns.forEach((column) => {
        const weekRange = column.value.split("-")[1];
        rowData[column.value] = {
          value: weekRange === subject.weekRange ? subject.value : 0,
          quote: "",
        };
      });

      if (subject.campaignableType === "module") {
        const row: SpecialInitiativesRow = {
          subject: subject.moduleTypology?.toUpperCase() || "",
          data: rowData,
          isTotal: false,
          kind: parent.mediaType,
          children: [],
        };
        rows.push(row);
      } else {
        const broadcastRow: SpecialInitiativesRow = {
          subject: subject.broadcastName || "",
          broadcastId: subject.broadcastId || -1,
          totalWeeklySeconds: subject.totalWeeklySeconds,
          data: rowData,
          isTotal: false,
          kind: parent.mediaType,
          children: [],
        };
        rows.push(broadcastRow);
      }
    }

    const a = this.groupBy(rows, "subject");
    rows = [];
    Object.keys(a).forEach((key) => {
      const items = a[key];
      const rowData: { [key: string]: { value: number; quote: string } } = {};
      items.forEach((item: SpecialInitiativesRow) => {
        Object.keys(item.data).forEach((o) => {
          if (rowData[o]) {
            rowData[o].value += item.data[o].value;
          } else {
            rowData[o] = {
              value: item.data[o].value,
              quote: "",
            };
          }
        });
      });

      const rowItem = items[0];
      rowItem.data = rowData;
      rows.push(rowItem);
    });

    return rows;
  }

  private groupBy(array: any[], key: string) {
    return array.reduce((result, currentValue) => {
      (result[currentValue[key]] = result[currentValue[key]] || []).push(
        currentValue
      );
      return result;
    }, {});
  }

  private computeParentTotals(
    parents: ParentCollection,
    tvTotals: CollectionData[],
    radioTotals: CollectionData[]
  ): CollectionData[] {
    const totals: CollectionData[] = [];
    for (const subject in parents) {
      const parent = parents[subject];
      parent.data = parent.data.reduce(
        (acc: CollectionData[], element: CollectionData) => {
          this.updateTotal(acc, element);
          if (parent.mediaType === "TV") {
            this.updateTotal(tvTotals, element);
          } else if (parent.mediaType === "RADIO") {
            this.updateTotal(radioTotals, element);
          }
          this.updateTotal(totals, element);
          return acc;
        },
        []
      );
    }
    return totals;
  }

  private updateTotal(totals: CollectionData[], element: CollectionData) {
    const total = totals.find((e) => e.weekRange == element.weekRange);
    if (!total) {
      if (!this.clientCount) {
        totals.push(Object.assign({}, element));
      } else {
        totals.push(
          Object.assign({}, element, { value: element.companyIds.size })
        );
      }
    } else {
      if (!this.clientCount) {
        total.value += element.value;
      } else {
        element.companyIds.forEach((companyId) => {
          total.companyIds.add(companyId);
        });
        total.value = total.companyIds.size;
      }
    }
  }

  private convertBroadcastersToParentsCollection(): ParentCollection {
    const parents: ParentCollection = {};
    for (const weekRange in this.value) {
      const records = this.value[weekRange];
      for (const record of records) {
        const key = this.detectionKey(record);
        const parent = parents[key] || {
          data: [],
          subjects: {},
          mediaType: record.mediaType,
        };
        const subject = parent.subjects[record.brandName] || [];
        this.updateSubjectAndParentData(subject, parent, record, weekRange);
        parent.subjects[record.brandName] = subject;
        parents[key] = parent;
      }
    }
    return parents;
  }

  private updateSubjectAndParentData(
    subject: CollectionData[],
    parent: ParentRecord,
    record: Broadcaster,
    weekRange: string
  ) {
    let value = 0;
    const companyIds = new Set<string>();
    if (
      this.unitOfMeasurement === "seconds" ||
      this.unitOfMeasurement === "quoted_seconds"
    ) {
      value = record.secondsCount;
    }
    if (
      this.unitOfMeasurement === "spots" ||
      this.unitOfMeasurement === "quoted_spots"
    ) {
      value = record.detectionsCount;
    }
    if (this.unitOfMeasurement === "clients_number") {
      value = record.companiesCount;
      record.companyIds.split(",").forEach((companyId) => {
        companyIds.add(companyId);
      });
    }

    const data = {
      weekRange,
      value,
      companyIds,
      quote: 0,
      moduleName: record.moduleName || "",
      moduleTypology: record.moduleTypology || "",
      broadcastName: record.broadcastName || "",
      campaignableType: record.campaignableType || "",
      broadcastId: record.broadcastId || -1,
      totalWeeklySeconds: record.secondsCount,
    };
    subject.push(data);
    parent.data.push(data);
  }

  private detectionKey(record: Broadcaster): string {
    if (this.subject === "sales_agencies") {
      return `${record.currentAgency}`;
    } else if (this.subject === "editors") {
      return `${record.publisher}`;
    }
    return `${record.marketSegment}`;
  }

  @Watch("value")
  onValueChanged() {
    this.checkPosition();
    if (this.columns.length > 1 && this.sortDirection === null) {
      this.sortColumn = this.columns[1].value;
      this.sortDirection = "DESC";
    } else {
      this.convertValueToRows();
    }
  }

  @Watch("subject")
  onSubjectChange() {
    this.columns = this.loadColumns();
    this.convertValueToRows();
  }

  @Watch("weekRanges", { deep: true })
  onWeekRangesChange() {
    this.columns = this.loadColumns();
    this.convertValueToRows();
  }

  @WindowEvent("resize")
  onResize() {
    if (!this.inner) {
      return;
    }
    this.checkPosition();
  }

  loadColumns(): SpecialInitiativesColumn[] {
    if (this.loading) {
      return [];
    }
    const columns: SpecialInitiativesColumn[] = [];
    switch (this.subject) {
      case "sales_agencies":
        columns.push({
          value: "subject",
          label: "Concessionarie",
          isHighlight: false,
        });
        break;
      case "editors":
        columns.push({
          value: "subject",
          label: "Editori",
          isHighlight: false,
        });
        break;
      case "market_segments":
        columns.push({
          value: "subject",
          label: "Segmenti di mercato",
          isHighlight: false,
        });
        break;
    }
    this.weekRanges.forEach((weekRange) => {
      columns.push({
        value: weekRange.name,
        label: weekRange.label,
        isHighlight: false,
      });
    });
    return columns;
  }

  protected scrollToLeft() {
    this.inner.scrollLeft = 0;
    this.checkPosition();
  }

  protected scrollToRight() {
    this.inner.scrollLeft = this.getTableWidth() - this.getElementWidth();
    this.checkPosition();
  }

  protected checkPosition() {
    this.position.overflow = this.getElementWidth() < this.getTableWidth();
    this.position.scrolledToRight =
      this.inner.scrollLeft == this.getTableWidth() - this.getElementWidth();
    this.position.scrolledToLeft = this.inner.scrollLeft == 0;
  }

  protected getElementWidth(): number {
    return this.inner?.clientWidth || 0;
  }

  protected getTableWidth(): number {
    return this.table?.clientWidth || 0;
  }

  private onBroadcastClick(broadcastId: number, totalWeeklySeconds: number) {
    this.$emit("broadcast_clicked", {
      broadcastId: broadcastId,
      totalWeeklySeconds: totalWeeklySeconds,
    });
  }
}
